import React, { useState, useEffect } from 'react';
import { Paper, Typography, Stack, Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import DateRangePicker from '../../../components/date-range-picker';
import { CallTrendChart, convertToCallChartData } from '..';
import { useGetDailyCallAnalytics } from '../../../../../../../features/analytics/hooks/useGetDailyCallAnalytics';
import { FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useExportDailyCallAnalytics } from '../../../../../../../features/analytics/hooks/exports/useExportDailyCallAnalytics';

const DailyCallTotals = ({ title, height = 380, fromDate: controlledFromDate, toDate: controlledToDate, onFromDateChange, onToDateChange }) => {
  const [localFromDate, setLocalFromDate] = useState(() => controlledFromDate || dayjs().startOf('month'));
  const [localToDate, setLocalToDate] = useState(() => controlledToDate || dayjs().endOf('month'));

  useEffect(() => {
    if (controlledFromDate) {
      setLocalFromDate(controlledFromDate);
    }
  }, [controlledFromDate]);

  useEffect(() => {
    if (controlledToDate) {
      setLocalToDate(controlledToDate);
    }
  }, [controlledToDate]);

  const queryFrom = localFromDate.format('YYYY-MM-DD');
  const queryTo = localToDate.format('YYYY-MM-DD');

  const { data, isFetching, error, refetch } = useGetDailyCallAnalytics({ from: queryFrom, to: queryTo });

  const chartData = data && data.success && data.data && data.data.dailyBreakdown
    ? convertToCallChartData(data.data.dailyBreakdown)
    : [];

  const exportDaily = useExportDailyCallAnalytics({
    onSuccess: (data) => {
      toast.success(`Daily call analytics exported: ${data.filename}`);
    },
    onError: (err) => {
      toast.error(`Export failed: ${err.message}`);
    }
  });

  const handleExport = () => {
    const params = { from: queryFrom, to: queryTo };
    toast.loading('Preparing export...', { id: 'daily-call-export' });
    exportDaily.mutate(params, {
      onSettled: () => {
        toast.dismiss('daily-call-export');
      }
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleFromDateChange = (date) => {
    const nextDate = date || dayjs().startOf('month');
    setLocalFromDate(nextDate);
    if (onFromDateChange) {
      onFromDateChange(nextDate);
    }
  };

  const handleToDateChange = (date) => {
    const nextDate = date || dayjs().endOf('month');
    setLocalToDate(nextDate);
    if (onToDateChange) {
      onToDateChange(nextDate);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 2, border: '1px solid #d0d0d0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <DateRangePicker
          fromDate={localFromDate}
          toDate={localToDate}
          onFromChange={handleFromDateChange}
          onToChange={handleToDateChange}
          disabled={isFetching || exportDaily.isLoading}
          fromLabel="From"
          toLabel="To"
          size="small"
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={handleRefresh}
            disabled={isFetching || exportDaily.isLoading}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={isFetching || exportDaily.isLoading || !chartData.length}
            size="small"
          >
            {exportDaily.isLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Stack>
      </Box>

      {isFetching ? (
        <Typography>Loading daily call totals...</Typography>
      ) : error ? (
        <Typography color="error">Error loading data: {error.message}</Typography>
      ) : (
        <CallTrendChart data={chartData} title={title} height={height} />
      )}
    </Paper>
  );
};

export default DailyCallTotals;
